import { useState, useEffect, useRef } from 'react';
import { 
  IconButton, 
  TextField, 
  Paper, 
  Typography, 
  Box, 
  Divider,
  List,
  ListItem,
  Button
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { streamChatMessage } from '../../api/chat';
import './Sidebar.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

function ChatMessage({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  
  return (
    <ListItem
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        padding: 1,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          padding: 2,
          maxWidth: '80%',
          backgroundColor: isUser ? 'rgba(137, 80, 252, 0.04)' : '#F5F5F5',
          borderRadius: '8px',
          border: isUser ? '1px solid #8950FC' : '1px solid #D9D9D9',
        }}
      >
        {isUser ? (
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => (
                  <Typography 
                    component="p" 
                    sx={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#8950FC',
                      fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
                      margin: '4px 0',
                    }}
                    {...props} 
                  />
                ),
                h1: ({ node, ...props }) => (
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      marginTop: '12px',
                      marginBottom: '8px',
                      color: '#8950FC',
                    }} 
                    {...props} 
                  />
                ),
                h2: ({ node, ...props }) => (
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      marginTop: '10px',
                      marginBottom: '6px',
                      color: '#8950FC',
                    }} 
                    {...props} 
                  />
                ),
                li: ({ node, ...props }) => (
                  <Typography 
                    component="li" 
                    sx={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      marginLeft: '20px',
                      color: '#8950FC',
                    }}
                    {...props} 
                  />
                ),
                code: ({ node, inline, ...props }) => (
                  inline ? (
                    <Typography 
                      component="code" 
                      sx={{
                        backgroundColor: 'rgba(137, 80, 252, 0.1)',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        color: '#8950FC',
                      }}
                      {...props} 
                    />
                  ) : (
                    <Box 
                      component="pre" 
                      sx={{
                        backgroundColor: 'rgba(137, 80, 252, 0.05)',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        borderLeft: '3px solid #8950FC',
                        overflowX: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        margin: '8px 0',
                      }}
                    >
                      <code {...props} />
                    </Box>
                  )
                ),
                // Table styling
                table: ({ node, ...props }) => (
                  <Box
                    sx={{
                      overflowX: 'auto',
                      margin: '12px 0',
                    }}
                  >
                    <table
                      style={{
                        borderCollapse: 'collapse',
                        width: '100%',
                        fontSize: '14px',
                      }}
                      {...props}
                    />
                  </Box>
                ),
                thead: ({ node, ...props }) => (
                  <thead style={{ backgroundColor: 'rgba(137, 80, 252, 0.08)' }} {...props} />
                ),
                tbody: ({ node, ...props }) => <tbody {...props} />,
                tr: ({ node, ...props }) => (
                  <tr
                    style={{
                      borderBottom: '1px solid #E8E8E8',
                    }}
                    {...props}
                  />
                ),
                th: ({ node, ...props }) => {
                  return (
                    <th
                      style={{
                        padding: '8px 12px',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: '#8950FC',
                        borderBottom: '2px solid #8950FC',
                      }}
                    >
                      {props.children}
                    </th>
                  );
                },
                td: ({ node, ...props }) => {
                  return (
                    <td
                      style={{
                        padding: '8px 12px',
                        textAlign: 'left',
                        verticalAlign: 'top',
                      }}
                    >
                      {props.children}
                    </td>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => (
                  <Typography 
                    component="p" 
                    sx={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#262626',
                      fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
                      margin: '4px 0',
                    }}
                    {...props} 
                  />
                ),
                h1: ({ node, ...props }) => (
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      marginTop: '12px',
                      marginBottom: '8px',
                      color: '#262626',
                    }} 
                    {...props} 
                  />
                ),
                h2: ({ node, ...props }) => (
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      marginTop: '10px',
                      marginBottom: '6px',
                      color: '#262626',
                    }} 
                    {...props} 
                  />
                ),
                li: ({ node, ...props }) => (
                  <Typography 
                    component="li" 
                    sx={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      marginLeft: '20px',
                      color: '#262626',
                    }}
                    {...props} 
                  />
                ),
                code: ({ node, inline, ...props }) => (
                  inline ? (
                    <Typography 
                      component="code" 
                      sx={{
                        backgroundColor: 'rgba(137, 80, 252, 0.1)',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        color: '#8950FC',
                      }}
                      {...props} 
                    />
                  ) : (
                    <Box 
                      component="pre" 
                      sx={{
                        backgroundColor: 'rgba(137, 80, 252, 0.05)',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        borderLeft: '3px solid #8950FC',
                        overflowX: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        margin: '8px 0',
                      }}
                    >
                      <code {...props} />
                    </Box>
                  )
                ),
                // Table styling
                table: ({ node, ...props }) => (
                  <Box
                    sx={{
                      overflowX: 'auto',
                      margin: '12px 0',
                    }}
                  >
                    <table
                      style={{
                        borderCollapse: 'collapse',
                        width: '100%',
                        fontSize: '14px',
                      }}
                      {...props}
                    />
                  </Box>
                ),
                thead: ({ node, ...props }) => (
                  <thead style={{ backgroundColor: 'rgba(137, 80, 252, 0.08)' }} {...props} />
                ),
                tbody: ({ node, ...props }) => <tbody {...props} />,
                tr: ({ node, ...props }) => (
                  <tr
                    style={{
                      borderBottom: '1px solid #E8E8E8',
                    }}
                    {...props}
                  />
                ),
                th: ({ node, ...props }) => {
                  return (
                    <th
                      style={{
                        padding: '8px 12px',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: '#8950FC',
                        borderBottom: '2px solid #8950FC',
                      }}
                    >
                      {props.children}
                    </th>
                  );
                },
                td: ({ node, ...props }) => {
                  return (
                    <td
                      style={{
                        padding: '8px 12px',
                        textAlign: 'left',
                        verticalAlign: 'top',
                      }}
                    >
                      {props.children}
                    </td>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </Paper>
    </ListItem>
  );
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  // Session storage key for messages
  const STORAGE_KEY = 'sidebarMessages';
  const [messages, setMessages] = useState<Message[]>(() => {
    // Try to load messages from session storage on initial render
    const savedMessages = sessionStorage.getItem(STORAGE_KEY);
    return savedMessages ? JSON.parse(savedMessages) : [];
  });
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node) && isOpen) {
        onClose();
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Scroll to bottom when messages change and save to session storage
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Save messages to session storage whenever they change
    if (messages.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  // Add welcome message when sidebar is opened if no messages exist
  useEffect(() => {
    if (isOpen) {
      // Only add welcome message if there are no existing messages
      if (messages.length === 0) {
        setMessages([
          {
            role: 'assistant',
            content: "Hi, I'm here to help you with your Dairy 2025 negotiation, feel free to ask any questions you have regarding the tenders or recommendations!"
          }
        ]);
      }
    }
    
    // Cleanup function to abort any ongoing requests when component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Function to clear chat history
  const handleClearChat = () => {
    // Clear messages from state
    setMessages([
      {
        role: 'assistant',
        content: "Hi, I'm here to help you with your Dairy 2025 negotiation, feel free to ask any questions you have regarding the tenders or recommendations!"
      }
    ]);
    
    // Clear messages from session storage
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: 'user' as const, content: inputValue.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Add an empty assistant message that will be updated with streaming content
    setMessages(prev => [...prev, { role: 'assistant' as const, content: '' }]);

    try {
      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      // Get all messages up to this point (excluding the empty assistant message we just added)
      const allMessages = messages.concat(userMessage);
      const response = await streamChatMessage(allMessages, abortControllerRef.current.signal);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Response body is null');

      let accumulatedContent = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        // Convert the Uint8Array to a string
        const chunk = new TextDecoder().decode(value);
        
        // The API returns lines of JSON, each ending with a newline
        const lines = chunk.split('\n').filter(line => line.trim() !== '');
        
        for (const line of lines) {
          try {
            const data = JSON.parse(line);
            // Extract the content from the response format
            if (data.choices && data.choices[0] && data.choices[0].messages) {
              const message = data.choices[0].messages[0];
              if (message.role === 'assistant' && message.content) {
                // Append to accumulated content instead of replacing it
                accumulatedContent += message.content;
                
                // Update the last message (which is the assistant's response)
                setMessages(prev => {
                  const newMessages = [...prev];
                  if (newMessages.length > 0) {
                    newMessages[newMessages.length - 1] = {
                      ...newMessages[newMessages.length - 1],
                      content: accumulatedContent
                    };
                  }
                  return newMessages;
                });
              }
            }
          } catch (e) {
            console.error('Error parsing JSON from stream:', e);
          }
        }
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error in chat stream:', error);
        // Update the last message with an error
        setMessages(prev => {
          const newMessages = [...prev];
          if (newMessages.length > 0) {
            newMessages[newMessages.length - 1] = {
              ...newMessages[newMessages.length - 1],
              content: 'Error: Something went wrong. Please try again.'
            };
          }
          return newMessages;
        });
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <Box
      ref={sidebarRef}
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        height: '100%',
        width: '48%',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.15)',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #E8E8E8',
          backgroundColor: '#F8F8F8',
        }}
      >
        <Typography 
          sx={{ 
            fontSize: '18px',
            fontWeight: 600,
            color: '#323130',
            fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
          }}
        >
          Negotiation Assistant
        </Typography>
        <IconButton 
          onClick={onClose} 
          aria-label="close"
          sx={{
            color: '#605E5C',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <List
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '16px',
          backgroundColor: '#FAFAFA',
        }}
      >
        {messages.map((message, index) => (
          <ChatMessage key={index} message={message} />
        ))}
        {isLoading && (
          <ListItem
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              padding: 1,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                padding: 2,
                maxWidth: '80%',
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
                border: '1px solid #8950FC',
              }}
            >
              <div className="loading-indicator-container">
                <span className="loading-indicator" style={{ backgroundColor: '#8950FC' }}></span>
                <span className="loading-indicator" style={{ backgroundColor: '#8950FC' }}></span>
                <span className="loading-indicator" style={{ backgroundColor: '#8950FC' }}></span>
              </div>
            </Paper>
          </ListItem>
        )}
        <div ref={messagesEndRef} />
      </List>

      {/* Suggested Questions */}
      <Box
        sx={{
          padding: '8px 16px',
          borderTop: '1px solid #E8E8E8',
          backgroundColor: 'rgba(137, 80, 252, 0.04)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#605E5C',
            marginBottom: '4px',
            fontWeight: 600,
          }}
        >
          Suggested questions:
        </Typography>
        <Button
          variant="text"
          size="small"
          disabled={isLoading}
          onClick={() => {
            // Create a synthetic user message and process it directly
            const questionText = "Can you help me write an email to the suppliers individually based on my outstanding actions?";
            
            // Add user message to chat
            const userMessage = { role: 'user' as const, content: questionText };
            setMessages(prev => [...prev, userMessage]);
            
            // Process the message using the existing handleSubmit logic
            // but without the form submission
            setIsLoading(true);
            
            // Add an empty assistant message that will be updated with streaming content
            setMessages(prev => [...prev, { role: 'assistant' as const, content: '' }]);
            
            // Use the same logic as handleSubmit but without the form event
            (async () => {
              try {
                // Create a new AbortController for this request
                abortControllerRef.current = new AbortController();
                // Get all messages up to this point (excluding the empty assistant message we just added)
                const allMessages = messages.concat(userMessage);
                const response = await streamChatMessage(allMessages, abortControllerRef.current.signal);
                
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body?.getReader();
                if (!reader) throw new Error('Response body is null');

                let accumulatedContent = '';
                
                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;
                  
                  // Convert the Uint8Array to a string
                  const chunk = new TextDecoder().decode(value);
                  
                  // The API returns lines of JSON, each ending with a newline
                  const lines = chunk.split('\n').filter(line => line.trim() !== '');
                  
                  for (const line of lines) {
                    try {
                      const data = JSON.parse(line);
                      // Extract the content from the response format
                      if (data.choices && data.choices[0] && data.choices[0].messages) {
                        const message = data.choices[0].messages[0];
                        if (message.role === 'assistant' && message.content) {
                          // Append to accumulated content instead of replacing it
                          accumulatedContent += message.content;
                          
                          // Update the last message (which is the assistant's response)
                          setMessages(prev => {
                            const newMessages = [...prev];
                            if (newMessages.length > 0) {
                              newMessages[newMessages.length - 1] = {
                                ...newMessages[newMessages.length - 1],
                                content: accumulatedContent
                              };
                            }
                            return newMessages;
                          });
                        }
                      }
                    } catch (e) {
                      console.error('Error parsing JSON from stream:', e);
                    }
                  }
                }
              } catch (error) {
                if ((error as Error).name !== 'AbortError') {
                  console.error('Error in chat stream:', error);
                  // Update the last message with an error
                  setMessages(prev => {
                    const newMessages = [...prev];
                    if (newMessages.length > 0) {
                      newMessages[newMessages.length - 1] = {
                        ...newMessages[newMessages.length - 1],
                        content: 'Error: Something went wrong. Please try again.'
                      };
                    }
                    return newMessages;
                  });
                }
              } finally {
                setIsLoading(false);
                abortControllerRef.current = null;
              }
            })();
          }}
          sx={{
            textTransform: 'none',
            justifyContent: 'flex-start',
            color: '#8950FC',
            padding: '2px 8px',
            fontSize: '13px',
            '&:hover': {
              backgroundColor: 'rgba(137, 80, 252, 0.08)',
            },
            width: '100%',
            textAlign: 'left',
          }}
        >
          Can you help me write an email to each of the suppliers individually based on my outstanding actions?
        </Button>
      </Box>

      <Box
        component="form"
        ref={formRef}
        onSubmit={handleSubmit}
        sx={{
          padding: '16px',
          borderTop: '1px solid #E8E8E8',
          display: 'flex',
          gap: '8px',
          backgroundColor: '#F8F8F8',
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={inputValue}
          onChange={handleInputChange}
          disabled={isLoading}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              '& fieldset': {
                borderColor: '#8C8C8C',
              },
              '&:hover fieldset': {
                borderColor: '#8950FC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8950FC',
              },
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Button
            onClick={handleClearChat}
            variant="outlined"
            disabled={isLoading || messages.length <= 1}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              borderColor: '#8950FC',
              color: '#8950FC',
              '&:hover': {
                borderColor: '#7339FB',
                backgroundColor: 'rgba(137, 80, 252, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: '#F3F2F1',
                color: '#A19F9D',
              },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '4px',
              padding: '6px 12px',
              minWidth: 'auto',
            }}
          >
            Clear
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!inputValue.trim() || isLoading}
            endIcon={<SendIcon />}
            sx={{
              backgroundColor: '#8950FC',
              '&:hover': {
                backgroundColor: '#7339FB',
              },
              '&.Mui-disabled': {
                backgroundColor: '#F3F2F1',
                color: '#A19F9D',
              },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '4px',
              padding: '6px 16px',
            }}
          >
            Send
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
